import React, {useEffect, useRef, useState} from 'react';
import {activeOptionIndex, listboxMoveKey, nextOptionIndex} from './option-list-state';

export type OptionListItem = {
    domId: string;
    value: number;
};

export type OptionListApi = {
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
    option: (value: number) => {
        id: string | undefined;
        tabIndex: 0 | -1;
        ariaSelected: boolean;
        onActivate: () => void;
        onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
    };
};

const plainSelectKey = (event: React.KeyboardEvent<HTMLElement>): boolean => {
    if (event.nativeEvent.isComposing || event.repeat) return false;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return false;
    return event.key === 'Enter' || event.key === ' ';
};

/**
 * One tab stop for a listbox. Arrows, Home, and End move that stop and do not select.
 * Enter and Space select once. Click selects and moves the stop to the clicked option.
 */
export function useOptionList(options: OptionListItem[], selected: number, onSelect: (value: number) => void): OptionListApi {
    const [cursor, setCursor] = useState<number | null>(null);
    const pendingFocus = useRef<string | null>(null);
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const values = options.map(option => option.value);
    const index = activeOptionIndex(values, selected, cursor);
    const optionsKey = values.join(',');

    useEffect(() => {
        const id = pendingFocus.current;
        if (!id || typeof document === 'undefined') return;
        pendingFocus.current = null;
        document.getElementById(id)?.focus();
    });

    useEffect(() => {
        if (cursor === null) return;
        if (!optionsRef.current.some(option => option.value === cursor)) setCursor(null);
    }, [cursor, optionsKey]);

    const moveTo = (next: number) => {
        const option = options[next];
        if (!option) return;
        pendingFocus.current = option.domId;
        if (cursor === option.value) {
            if (typeof document !== 'undefined') document.getElementById(option.domId)?.focus();
            pendingFocus.current = null;
            return;
        }
        setCursor(option.value);
    };

    const selectValue = (value: number) => {
        setCursor(value);
        onSelect(value);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.nativeEvent.isComposing) return;
        const moveKey = listboxMoveKey(event);
        if (moveKey) {
            if (index < 0) return;
            const next = nextOptionIndex(index, options.length, moveKey);
            if (next == null) return;
            event.preventDefault();
            if (next !== index) moveTo(next);
            return;
        }
        if (plainSelectKey(event) && event.target === event.currentTarget) {
            const option = options[index];
            if (!option) return;
            event.preventDefault();
            selectValue(option.value);
        }
    };

    const option = (value: number): ReturnType<OptionListApi['option']> => {
        const optionIndex = values.indexOf(value);
        const item = optionIndex >= 0 ? options[optionIndex] : undefined;
        return {
            id: item?.domId,
            tabIndex: optionIndex >= 0 && optionIndex === index ? 0 : -1,
            ariaSelected: value === selected,
            onActivate: () => selectValue(value),
            onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
                if (!plainSelectKey(event)) return;
                event.preventDefault();
                event.stopPropagation();
                selectValue(value);
            },
        };
    };

    return {onKeyDown, option};
}

type OptionListProps = {
    options: OptionListItem[];
    selected: number;
    onSelect: (value: number) => void;
    children: (api: OptionListApi) => React.ReactNode;
};

/** Render-prop for class components, which cannot call the hook themselves. Adds no DOM node. */
export const OptionList: React.FC<OptionListProps> = ({options, selected, onSelect, children}) => {
    const api = useOptionList(options, selected, onSelect);
    return <>{children(api)}</>;
};
